import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import type {
  DebugData,
  GetLoginsRequestPayload,
  GetLoginsResponse,
  GetReportRequestPayload,
  ReportResponseType,
} from "./types";

export const useGetLogins = (payload: GetLoginsRequestPayload) => {
  return useQuery<GetLoginsResponse>({
    queryKey: [queryKeys.getLogins, payload],
    queryFn: async () => api.post("/logins/get-logins", payload).then((res) => res.data),
  });
};

export const useGetInfinityLogins = (payload: Omit<GetLoginsRequestPayload, "page">) => {
  return useInfiniteQuery<GetLoginsResponse>({
    queryKey: [queryKeys.getLogins, "infinite", payload],
    queryFn: async ({ pageParam = 1 }) =>
      api.post("/logins/get-logins", { ...payload, page: pageParam }).then((res) => res.data),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.pagination.total / lastPage.pagination.pageSize);
      if (lastPage.pagination.page < totalPages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
};

export const useGetReport = () => {
  return useMutation({
    mutationFn: async (payload: GetReportRequestPayload) => {
      // eslint-disable-next-line
      const preparedPayload: any = { ...payload };
      const keys = Object.keys(preparedPayload);

      const searchParams = new URLSearchParams();
      for (const key of keys) {
        if (!preparedPayload[key]) {
          continue;
        }

        const value = preparedPayload[key];
        if (Array.isArray(value)) {
          value.forEach((valueItem) => searchParams.append(key, valueItem));
        } else {
          searchParams.append(key, value);
        }
      }

      return api
        .get<ReportResponseType>(`/logins/get-report-export?${searchParams.toString()}`)
        .then((res) => res.data);
    },
  });
};

export const useGetDebugInfo = (loginId: number, enabled = false) => {
  return useQuery<DebugData>({
    enabled,
    queryKey: [queryKeys.debugInfo, loginId],
    queryFn: async () => api.get(`/logins/debug/${loginId}`).then((res) => res.data),
  });
};

export const useGetBetsSports = () => {
  return useQuery<string[]>({
    queryKey: [queryKeys.getBetsSports],
    queryFn: async () => api.get("/logins/bets/sports").then((res) => res.data),
  });
};
